import { parseTimestamp } from './timestamp'
import type { CatalogEntry } from './catalog'
import { softwareKey } from './normalise'
import type { Report } from './types'

export interface SyncPlan {
  allEntries: CatalogEntry[]
  newEntries: CatalogEntry[]
  alreadyLoadedCount: number
}

/**
 * Compares discovered catalog entries against current reports to find which
 * assessments are new and need to be fetched.
 *
 * @param dismissed Addresses the user removed; never brought back by a sync.
 */
export function planSync(
  entries: CatalogEntry[],
  currentReports: Report[],
  dismissed: readonly string[] = [],
): SyncPlan {
  const skipped = new Set(dismissed)
  const existingRefs = new Set<string>()
  // Software and the exact instant it was assessed. Not the day: two runs on
  // one day are two assessments, and the second would never be fetched.
  const existingKeys = new Set<string>()

  for (const report of currentReports) {
    if (report.source?.ref) {
      existingRefs.add(report.source.ref)
    }
    // Read again from what the report wrote, the same way an entry's time is read.
    const at = parseTimestamp(report.createdAt?.original)?.value ?? report.createdAt?.value
    if (at !== undefined) existingKeys.add(`${report.software.key}::${at}`)
  }

  const seenUrlsInBatch = new Set<string>()
  const newEntries = entries.filter((entry) => {
    if (seenUrlsInBatch.has(entry.reportUrl)) return false
    seenUrlsInBatch.add(entry.reportUrl)

    if (existingRefs.has(entry.reportUrl) || skipped.has(entry.reportUrl)) return false
    const at = parseTimestamp(entry.assessedAt)?.value
    if (at === undefined) return true
    return !existingKeys.has(`${softwareKey(entry.softwareName, entry.repositoryUrl)}::${at}`)
  })

  return {
    allEntries: entries,
    newEntries,
    alreadyLoadedCount: entries.length - newEntries.length,
  }
}
